import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShieldCheck, LogOut, Loader2, Satellite, ArrowLeft, RefreshCcw, Zap,
  CheckCircle2, AlertTriangle, XCircle, Link2, Package, Clock,
} from 'lucide-react';
import { adminApi, clearAdminToken } from '../../services/adminApi';
import { ScheduledJobsCard } from '../../components/admin/ScheduledJobsCard';
import { ReadinessEmailHoldCard } from '../../components/admin/ReadinessEmailHoldCard';

const POLL_INTERVAL_MS = 15000;

const formatRelative = (seconds) => {
  if (seconds == null) return 'never';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

const HEALTH_CFG = {
  healthy: { Icon: CheckCircle2, cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30', label: 'Healthy' },
  silent:  { Icon: AlertTriangle, cls: 'text-amber-400 bg-amber-500/10 border-amber-500/30', label: 'Silent' },
  errored: { Icon: XCircle, cls: 'text-red-400 bg-red-500/10 border-red-500/30', label: 'Errored' },
};

const STATUS_LABEL = {
  connected: 'Connected',
  invalid_key: 'Invalid key',
  unreachable: 'Unreachable',
  disconnected: 'Disconnected',
};

const HealthPill = ({ health }) => {
  const cfg = HEALTH_CFG[health] || HEALTH_CFG.errored;
  const { Icon, cls, label } = cfg;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-[10px] font-mono uppercase tracking-widest ${cls}`}
      data-testid={`vdaq-health-pill-${health}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const Metric = ({ label, value, sub, testid, tone = 'default' }) => (
  <div
    className={`p-4 rounded-md border ${
      tone === 'warn'
        ? 'border-amber-500/40 bg-amber-500/5'
        : tone === 'bad'
        ? 'border-red-500/40 bg-red-500/5'
        : tone === 'good'
        ? 'border-emerald-500/40 bg-emerald-500/5'
        : 'border-border bg-secondary/30'
    }`}
    data-testid={testid}
  >
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="text-2xl font-mono font-semibold mt-1">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const AdminVdaqHealthPage = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await adminApi.vdaqHealth();
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearAdminToken();
        navigate('/admin/login', { replace: true });
      } else if (!silent) {
        toast.error('Failed to load V-DAQ connect health');
      }
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const meRes = await adminApi.me();
        if (!cancelled) setMe(meRes.data);
      } catch {
        clearAdminToken();
        navigate('/admin/login', { replace: true });
        return;
      }
      load();
    };
    init();
    const interval = setInterval(() => load(true), POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [load, navigate]);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await adminApi.vdaqSyncNow();
      toast.success(`Synced ${data.tenants ?? 0} tenant(s)`);
      await load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    navigate('/admin/login', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const tenants = data?.tenants || [];
  const silentAfter = data?.silent_after_minutes ?? 30;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-vdaq-health-page">

      {/* Header */}
      <header className="border-b border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 text-sm"
              data-testid="back-to-admin"
            >
              <ArrowLeft className="w-4 h-4" /> Tenants
            </button>
            <div className="w-px h-5 bg-border" />
            <div className="flex items-center gap-2">
              <Satellite className="w-5 h-5 text-primary" />
              <h1 className="font-semibold tracking-tight text-lg">V-DAQ Connect Health</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden md:inline" data-testid="admin-identity">
              <ShieldCheck className="w-3 h-3 inline mr-1" />
              {me?.email}
            </span>
            <button
              onClick={handleSyncNow}
              disabled={syncing}
              className="text-xs px-3 py-1.5 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-1.5 disabled:opacity-50"
              data-testid="vdaq-sync-now"
            >
              {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
              {syncing ? 'Syncing…' : 'Sync all now'}
            </button>
            <button
              onClick={() => load()}
              disabled={refreshing}
              className="text-xs px-3 py-1.5 rounded-sm border border-border hover:bg-secondary transition-colors flex items-center gap-1.5"
              data-testid="refresh-status"
            >
              <RefreshCcw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              data-testid="admin-logout"
            >
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Summary tiles */}
        <section className="space-y-3">
          <h2 className="text-sm uppercase tracking-widest text-muted-foreground">Across all tenants</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric
              label="Connected"
              value={summary.total_connected ?? 0}
              sub="Tenants with a V-DAQ key"
              testid="vdaq-stat-connected"
            />
            <Metric
              label="Healthy"
              value={summary.healthy ?? 0}
              sub={`Synced within ${silentAfter}m`}
              testid="vdaq-stat-healthy"
              tone={(summary.healthy || 0) > 0 ? 'good' : 'default'}
            />
            <Metric
              label="Silent links"
              value={summary.silent ?? 0}
              sub={`No sync for > ${silentAfter}m`}
              testid="vdaq-stat-silent"
              tone={(summary.silent || 0) > 0 ? 'warn' : 'default'}
            />
            <Metric
              label="Errored"
              value={summary.errored ?? 0}
              sub="Bad key or unreachable"
              testid="vdaq-stat-errored"
              tone={(summary.errored || 0) > 0 ? 'bad' : 'default'}
            />
          </div>
        </section>

        {/* Scheduled jobs heartbeat */}
        <ScheduledJobsCard />

        {/* Readiness change emails held after a deploy */}
        <ReadinessEmailHoldCard />

        {/* Tenant table */}
        <section className="space-y-3">
          <h2 className="text-sm uppercase tracking-widest text-muted-foreground">
            Per-tenant status · silent &amp; errored first
          </h2>
          <div className="rounded-md border border-border overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-secondary/40 border-b border-border">
                <tr className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="text-left px-3 py-2">Tenant</th>
                  <th className="text-left px-3 py-2">Health</th>
                  <th className="text-left px-3 py-2">V-DAQ status</th>
                  <th className="text-left px-3 py-2">Last sync</th>
                  <th className="text-right px-3 py-2">Linked assets</th>
                </tr>
              </thead>
              <tbody>
                {tenants.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-12 text-center text-muted-foreground text-sm">
                      <Satellite className="w-6 h-6 mx-auto mb-2 opacity-30" />
                      No tenants have connected V-DAQ yet.
                    </td>
                  </tr>
                ) : (
                  tenants.map((t) => (
                    <tr
                      key={t.id}
                      className="border-t border-border hover:bg-secondary/20"
                      data-testid={`vdaq-tenant-row-${t.client_id}`}
                    >
                      <td className="px-3 py-2.5">
                        <div className="font-medium">{t.name}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{t.client_id}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <HealthPill health={t.health} />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-xs">{STATUS_LABEL[t.status] || t.status}</div>
                        {t.status_message && (
                          <div className="text-[11px] text-red-400/80 mt-0.5 max-w-xs truncate" title={t.status_message}>
                            {t.status_message}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1.5 font-mono text-xs">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          {formatRelative(t.seconds_since_sync)}
                        </span>
                        {t.last_sync_at && (
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            {new Date(t.last_sync_at).toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <span className="inline-flex items-center gap-1.5 font-mono text-xs">
                          {t.linked_assets > 0 ? <Link2 className="w-3 h-3 text-emerald-400" /> : <Package className="w-3 h-3 text-muted-foreground" />}
                          {t.linked_assets}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminVdaqHealthPage;
